import api from '@/lib/api';
import type {
  ScheduledReport,
  ReportFrequency,
  DataExtractType,
  DataExtractResult,
  RevenueStatistics,
  RoomSoldStatistics,
} from '@pms/shared';

// ─── Scheduler ────────────────────────────────────────────────────────────────

export interface CreateScheduledReportPayload {
  propertyId: string;
  name: string;
  reportType: string;
  frequency: ReportFrequency;
  recipients?: string[] | undefined;
  isActive?: boolean | undefined;
}

export interface UpdateScheduledReportPayload {
  name?: string | undefined;
  reportType?: string | undefined;
  frequency?: ReportFrequency | undefined;
  recipients?: string[] | undefined;
  isActive?: boolean | undefined;
}

export async function fetchScheduledReports(
  propertyId?: string,
): Promise<ScheduledReport[]> {
  const { data } = await api.get<ScheduledReport[]>('/reports/schedules', {
    params: propertyId ? { propertyId } : undefined,
  });
  return data;
}

export async function createScheduledReport(
  payload: CreateScheduledReportPayload,
): Promise<ScheduledReport> {
  const { data } = await api.post<ScheduledReport>('/reports/schedules', payload);
  return data;
}

export async function updateScheduledReport(
  id: string,
  payload: UpdateScheduledReportPayload,
): Promise<ScheduledReport> {
  const { data } = await api.patch<ScheduledReport>(
    `/reports/schedules/${id}`,
    payload,
  );
  return data;
}

export async function deleteScheduledReport(id: string): Promise<void> {
  await api.delete(`/reports/schedules/${id}`);
}

export async function runScheduledReport(id: string): Promise<ScheduledReport> {
  const { data } = await api.post<ScheduledReport>(
    `/reports/schedules/${id}/run`,
  );
  return data;
}

// ─── Data Extracts / Custom Exports ───────────────────────────────────────────

export interface DataExtractQuery {
  extractType: DataExtractType;
  propertyId: string;
  from: string;
  to: string;
}

export async function runDataExtract(
  query: DataExtractQuery,
): Promise<DataExtractResult> {
  const { data } = await api.post<DataExtractResult>('/reports/extracts', query);
  return data;
}

export async function downloadDataExtractCsv(
  query: DataExtractQuery,
): Promise<void> {
  const { extractType, ...params } = query;
  const response = await api.get(`/reports/extracts/${extractType}/export.csv`, {
    params,
    responseType: 'blob',
  });
  const blob = new Blob([response.data as BlobPart], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${extractType}_${query.from}_${query.to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Statistics ────────────────────────────────────────────────────────────────

export interface StatisticsQuery {
  propertyId: string;
  from: string;
  to: string;
}

export async function fetchRevenueStatistics(
  query: StatisticsQuery,
): Promise<RevenueStatistics> {
  const { data } = await api.get<RevenueStatistics>(
    '/reports/statistics/revenue',
    { params: query },
  );
  return data;
}

export async function fetchRoomSoldStatistics(
  query: StatisticsQuery,
): Promise<RoomSoldStatistics> {
  const { data } = await api.get<RoomSoldStatistics>(
    '/reports/statistics/rooms-sold',
    { params: query },
  );
  return data;
}
